import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  VStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  useColorModeValue,
  Spinner,
  SimpleGrid,
  Grid,
  GridItem,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { SessionContext } from "./SessionContext";
import { supabase } from "./supabaseClient";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

function Dashboard() {
  const { user } = useContext(SessionContext);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState(user?.role === "admin" ? "admin" : "satis");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [reportText, setReportText] = useState("");
  const toast = useToast();

  const bgColor = useColorModeValue("gray.50", "gray.700");
  const cardBgColor = useColorModeValue("white", "gray.600");
  const textColor = useColorModeValue("gray.800", "white");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endDatePlusOne = new Date(endDate);
      endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
      const endDatePlusOneStr = endDatePlusOne.toISOString().split("T")[0];

      const [customerData, userData, dailyactivityData, customerActivityData] =
        await Promise.all([
          supabase
            .from("customers")
            .select("*")
            .gte("created_at", startDate)
            .lte("created_at", endDatePlusOneStr),
          supabase.from("users_data").select("*").eq("position", userType),
          supabase
            .from("daily_activities")
            .select("*")
            .gte("created_at", startDate)
            .lte("created_at", endDatePlusOneStr),
          supabase
            .from("customer_activities")
            .select("*")
            .gte("created_at", startDate)
            .lte("created_at", endDatePlusOneStr),
        ]);

      const processedData = processData(
        customerData.data,
        userData.data,
        dailyactivityData.data,
        customerActivityData.data,
        userType
      );
      setData(processedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, userType]);

  const generateReport = async () => {
    if (!startDate || !endDate) {
      toast({
        title: "Tarih seçiniz",
        description: "Lütfen başlangıç ve bitiş tarihlerini seçin.",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      const endDatePlusOne = new Date(endDate);
      endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);
      const endDatePlusOneStr = endDatePlusOne.toISOString().split("T")[0];

      // Fetch activities with customer and user data for the report
      const { data: activities, error } = await supabase
        .from("customer_activities")
        .select("*, customers(*), users_data(*), customer_attaches(*)")
        .gte("created_at", startDate)
        .lte("created_at", endDatePlusOneStr)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching report data:", error);
        throw error;
      }

      // For admin: show all team activities (matches chart behavior)
      // For non-admin: show only their own position's activities
      const filteredActivities = user?.role === "admin"
        ? activities
        : activities.filter((a) => a.users_data?.position === userType);

      const text = formatWhatsAppReport(filteredActivities, startDate, endDate, userType);
      setReportText(text);
      onOpen();
    } catch (err) {
      console.error("Rapor oluşturma hatası:", err);
      toast({
        title: "Hata",
        description: "Rapor oluşturulurken bir hata oluştu.",
        status: "error",
        duration: 3000,
      });
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [fetchData, startDate, endDate, userType]);

  const processData = (
    customers,
    users,
    dailyactivityData,
    customerActivityData,
    userType
  ) => {
    const safeCustomers = customers || [];
    const safeUsers = users || [];
    const safeDailyActivity = dailyactivityData || [];
    const safeCustomerActivity = customerActivityData || [];

    const dailyCustomers = safeCustomers.reduce((acc, customer) => {
      const date = customer.created_at.split("T")[0];
      acc[date] = (acc[date] || 0) + 1;
      return acc;
    }, {});

    const userActivities = safeUsers.map((user) => ({
      id: user.user_uid,
      name: user.name + " " + user.last_name,
      customersCreated: safeCustomers.filter(
        (c) => c.created_from === user.user_uid && user.position === userType
      ).length,
      dailyActivities: safeDailyActivity.filter(
        (a) => a.user_id === user.user_uid && user.position === userType
      ).length,
      customerActivities: safeCustomerActivity.filter(
        (a) => a.created_from === user.user_uid && user.position === userType
      ).length,
      position: user.position,
    }));

    const customerCities = safeCustomers.reduce((acc, customer) => {
      acc[customer.city] = (acc[customer.city] || 0) + 1;
      return acc;
    }, {});

    return {
      dailyCustomers: Object.entries(dailyCustomers)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(a.date) - new Date(b.date)),
      userActivities,
      customerCities: Object.entries(customerCities).map(([city, count]) => ({
        city,
        count,
      })),
    };
  };

  const formatWhatsAppReport = (activities, startDate, endDate, userType) => {
    const formatDate = (dateStr) => {
      const d = new Date(dateStr);
      return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    const dateDisplay = startDate === endDate
      ? formatDate(endDate)
      : `${formatDate(startDate)} - ${formatDate(endDate)}`;

    // Get first user name from activities for this user type
    const firstActivity = activities[0];
    const userName = firstActivity
      ? `${firstActivity.users_data?.name || ""} ${firstActivity.users_data?.last_name || ""}`
      : userType;

    let report = `📊 GÜNLÜK SAHA RAPORU (${dateDisplay})\n`;
    report += `👤 Personel: ${userName}\n`;
    report += `━━━━━━━━━━━━━━━━━━━━━━━━\n`;

    if (activities.length === 0) {
      report += `\n⚠️ Bu tarih aralığında işlem bulunamadı.\n`;
      return report;
    }

    const titleCounts = {};

    activities.forEach((act) => {
      const customerName = act.customers?.name || "Bilinmeyen";
      const pending = act.customers?.status === "pending" ? " ⏳ (Onay Bekliyor)" : "";
      report += `\n🏢 ${customerName}${pending}\n`;
      report += `💬 ${act.title}: ${act.activity}\n`;
      if (act.customer_attaches?.length > 0) {
        report += `📎 Dosya: Var\n`;
      }

      const titleKey = act.title.toUpperCase();
      titleCounts[titleKey] = (titleCounts[titleKey] || 0) + 1;
    });

    report += `\n━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    report += `📈 Toplam: `;
    report += Object.entries(titleCounts)
      .map(([title, count]) => `${count} ${title}`)
      .join(", ");
    report += `\n`;
    report += `🔢 Toplam İşlem: ${activities.length}\n`;

    return report;
  };

  const dailyCustomersChartData = {
    labels: data ? data.dailyCustomers.map((d) => d.date) : [],
    datasets: [
      {
        label: "Günlük Müşteri Sayısı",
        data: data ? data.dailyCustomers.map((d) => d.count) : [],
        backgroundColor: "rgba(75, 192, 192, 0.6)",
      },
    ],
  };

  const userActivitiesChartData = {
    labels: data ? data.userActivities.map((u) => u.name) : [],
    datasets: [
      {
        label: "Günlük Aktiviteler",
        data: data ? data.userActivities.map((u) => u.dailyActivities) : [],
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
      {
        label: "Müşteri Aktiviteleri",
        data: data ? data.userActivities.map((u) => u.customerActivities) : [],
        backgroundColor: "rgba(255, 206, 86, 0.6)",
      },
    ],
  };

  const userCustomersChartData = {
    labels: data ? data.userActivities.map((u) => u.name) : [],
    datasets: [
      {
        label: "Oluşturulan Müşteri Sayısı",
        data: data ? data.userActivities.map((u) => u.customersCreated) : [],
        backgroundColor: "rgba(153, 102, 255, 0.6)",
      },
    ],
  };

  const customerCitiesChartData = {
    labels: data ? data.customerCities.map((c) => c.city) : [],
    datasets: [
      {
        data: data ? data.customerCities.map((c) => c.count) : [],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
  };

  const userActivitiesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
  };

  return (
    <Box p={5} bg={bgColor} color={textColor}>
      <VStack spacing={8} align="stretch">
        <Heading>Müşteri ve İşlem Özeti</Heading>

        <Grid
          gap={2}
          alignContent={"center"}
          templateColumns={{ base: "repeat(1, 1fr)", lg: user?.role === "admin" ? "repeat(3, 1fr)" : "repeat(2, 1fr)" }}
        >
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel>Başlangıç Tarihi</FormLabel>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel>Bitiş Tarihi</FormLabel>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </FormControl>
          </GridItem>
          {user?.role === "admin" && (
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel>Kullanıcı Tipi</FormLabel>
                <Select
                  value={userType}
                  onChange={(e) => setUserType(e.target.value)}
                >
                  <option value="admin">Admin</option>
                  <option value="satis">Satış</option>
                  <option value="servis">Servis</option>
                </Select>
              </FormControl>
            </GridItem>
          )}
        </Grid>
        <Button
          colorScheme="blue"
          onClick={generateReport}
          isLoading={loading}
          loadingText="Rapor Oluşturuluyor"
        >
          Rapor Oluştur
        </Button>

        {loading ? (
          <Spinner />
        ) : data ? (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8}>
            <Box bg={cardBgColor} p={5} borderRadius="md" boxShadow="md">
              <Heading size="md" mb={4}>
                Günlük Oluşturulan Müşteri Sayısı
              </Heading>
              <Box height="300px">
                <Bar data={dailyCustomersChartData} options={chartOptions} />
              </Box>
            </Box>
            {user?.role === "admin" && (
              <Box bg={cardBgColor} p={5} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>
                  Kullanıcı Aktiviteleri
                </Heading>
                <Box height="300px">
                  <Bar
                    data={userActivitiesChartData}
                    options={userActivitiesChartOptions}
                  />
                </Box>
              </Box>
            )}
            {user?.role === "admin" && (
              <Box bg={cardBgColor} p={5} borderRadius="md" boxShadow="md">
                <Heading size="md" mb={4}>
                  Kullanıcı Başına Oluşturulan Müşteri Sayısı
                </Heading>
                <Box height="300px">
                  <Bar data={userCustomersChartData} options={chartOptions} />
                </Box>
              </Box>
            )}
            <Box bg={cardBgColor} p={5} borderRadius="md" boxShadow="md">
              <Heading size="md" mb={4}>
                Müşteri Şehirleri
              </Heading>
              <Box height="300px">
                <Pie data={customerCitiesChartData} options={chartOptions} />
              </Box>
            </Box>
          </SimpleGrid>
        ) : null}
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent mx={5}>
          <ModalHeader>WhatsApp Raporu Hazır</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              value={reportText}
              onChange={(e) => setReportText(e.target.value)}
              rows={16}
              fontFamily="mono"
              fontSize="sm"
              whiteSpace="pre-wrap"
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              colorScheme="teal"
              onClick={() => {
                navigator.clipboard.writeText(reportText);
                toast({ title: "Kopyalandı!", status: "success", duration: 2000 });
              }}
            >
              Kopyala
            </Button>
            <Button
              colorScheme="green"
              onClick={() => {
                const url = 'https://api.whatsapp.com/send?text=' + encodeURIComponent(reportText);
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              WhatsApp'a Gönder
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Kapat
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Dashboard;
